/**
 * The /plans filters live in the URL so a filtered view can be linked, shared
 * and reloaded. The page used to hold every filter in React state only, which
 * meant a reload dropped back to the unfiltered list and a campaign could not
 * point at, say, STC prepaid plans under 200 SAR.
 *
 * This module is the only place that knows the query-string spelling. The
 * page and the filter panel work with `PlansFilterState`; encoding lives here
 * so the two sides cannot drift apart.
 *
 *   /plans?providers=1,3&data=50000,-1&type=1&priceFrom=100&priceTo=200&sort=price_asc&page=2
 */

export type PlansFilterState = {
  providerId: number[];
  mobileDate: number[];
  callsMin: number[];
  contractTypeId: number[];
  /** 0 means "any"; otherwise the number of days (30 / 60 / 90). */
  validity: number;
  priceFrom: number;
  priceTo: number;
  /** Free-text search over the plan name. */
  name: string;
  orderBy: string;
  /** 1-based, matching the pager and the API's `offset`. */
  page: number;
  discount: boolean;
};

/**
 * The unfiltered list. `priceTo` is the same ceiling the price input starts
 * from — the API needs a bound, so there is no "no maximum" to send.
 */
export const DEFAULT_PLANS_FILTER: PlansFilterState = {
  providerId: [],
  mobileDate: [],
  callsMin: [],
  contractTypeId: [],
  validity: 0,
  priceFrom: 0,
  priceTo: 10000,
  name: "",
  orderBy: "",
  page: 1,
  discount: false,
};

/** Query-string names, deliberately shorter than the API's field names. */
const KEYS = {
  providerId: "providers",
  mobileDate: "data",
  callsMin: "calls",
  contractTypeId: "type",
  validity: "validity",
  priceFrom: "priceFrom",
  priceTo: "priceTo",
  name: "q",
  orderBy: "sort",
  page: "page",
  discount: "discount",
};

/** Sort values the page offers; anything else is ignored rather than sent on. */
const ORDER_BY = ["price_asc", "price_desc", "name_asc", "name_desc"];

/**
 * `-1` is a real id (it is how the API spells "unlimited"), so only empty and
 * non-numeric entries are dropped.
 */
const readNumberList = (raw: string | null): number[] => {
  if (!raw) return [];
  return raw
    .split(",")
    .map((part) => parseInt(part.trim(), 10))
    .filter((value) => !Number.isNaN(value));
};

const readNumber = (raw: string | null, fallback: number): number => {
  if (raw === null || raw.trim() === "") return fallback;
  const value = parseInt(raw, 10);
  return Number.isNaN(value) ? fallback : value;
};

/**
 * Builds the filter a URL describes. Every field falls back to its default, so
 * a hand-edited or truncated link still renders a working page rather than an
 * error.
 */
export const readPlansFilter = (params: URLSearchParams): PlansFilterState => {
  const orderBy = params.get(KEYS.orderBy) || "";
  const priceFrom = Math.max(
    0,
    readNumber(params.get(KEYS.priceFrom), DEFAULT_PLANS_FILTER.priceFrom)
  );
  const priceTo = readNumber(
    params.get(KEYS.priceTo),
    DEFAULT_PLANS_FILTER.priceTo
  );

  return {
    providerId: readNumberList(params.get(KEYS.providerId)),
    mobileDate: readNumberList(params.get(KEYS.mobileDate)),
    callsMin: readNumberList(params.get(KEYS.callsMin)),
    contractTypeId: readNumberList(params.get(KEYS.contractTypeId)),
    validity: readNumber(params.get(KEYS.validity), 0),
    priceFrom,
    // A reversed range returns nothing at all, which reads as a broken page;
    // treat it as "no maximum" instead.
    priceTo: priceTo < priceFrom ? DEFAULT_PLANS_FILTER.priceTo : priceTo,
    name: params.get(KEYS.name) || "",
    orderBy: ORDER_BY.includes(orderBy) ? orderBy : "",
    page: Math.max(1, readNumber(params.get(KEYS.page), 1)),
    discount: params.get(KEYS.discount) === "1",
  };
};

/**
 * Writes the filter back into the query string.
 *
 * Only the keys above are touched: `language` and `token` are read by
 * UrlSession in App.tsx on every route, and a share link that dropped the
 * language would send an Arabic user to an English page. Fields left at their
 * default are removed rather than spelled out, so an unfiltered /plans stays a
 * bare /plans.
 */
export const writePlansFilter = (
  current: URLSearchParams,
  state: PlansFilterState
): URLSearchParams => {
  const params = new URLSearchParams(current);

  const set = (key: string, value: string | null) => {
    if (value === null || value === "") params.delete(key);
    else params.set(key, value);
  };

  const list = (values: number[]) => (values.length ? values.join(",") : null);

  set(KEYS.providerId, list(state.providerId));
  set(KEYS.mobileDate, list(state.mobileDate));
  set(KEYS.callsMin, list(state.callsMin));
  set(KEYS.contractTypeId, list(state.contractTypeId));
  set(KEYS.validity, state.validity ? String(state.validity) : null);
  set(
    KEYS.priceFrom,
    state.priceFrom !== DEFAULT_PLANS_FILTER.priceFrom
      ? String(state.priceFrom)
      : null
  );
  set(
    KEYS.priceTo,
    state.priceTo !== DEFAULT_PLANS_FILTER.priceTo
      ? String(state.priceTo)
      : null
  );
  set(KEYS.name, state.name.trim() || null);
  set(KEYS.orderBy, ORDER_BY.includes(state.orderBy) ? state.orderBy : null);
  set(KEYS.page, state.page > 1 ? String(state.page) : null);
  set(KEYS.discount, state.discount ? "1" : null);

  return params;
};

/**
 * Which of the page's own controls to render.
 *
 * A campaign that buys traffic for one provider lands on a link that both
 * filters to that provider and hides the controls which would widen the list
 * again, e.g. /plans?providers=14&noproviders. The filters still apply — these
 * only decide what the visitor can change.
 *
 * Written as bare flags so a marketing link can carry them without a value,
 * and read with `has` rather than `get` for the same reason: `&noproviders`
 * and `&noproviders=1` both count.
 *
 * None of these keys are touched by `writePlansFilter`, so they survive every
 * filter, sort and page change the visitor makes.
 */
export type PlansChrome = {
  /** The whole left-hand filter panel, and the button that opens it on mobile. */
  showFilters: boolean;
  /** Just the provider list inside the panel; the other filters stay. */
  showProviderFilter: boolean;
  /** The search box, which can otherwise reach any plan by name. */
  showSearch: boolean;
};

export const readPlansChrome = (params: URLSearchParams): PlansChrome => ({
  showFilters: !params.has("nofilters"),
  showProviderFilter: !params.has("nofilters") && !params.has("noproviders"),
  showSearch: !params.has("nosearch"),
});

/** True when the page is showing less than the full list of controls. */
export const isChromeLimited = (chrome: PlansChrome): boolean =>
  !chrome.showFilters || !chrome.showProviderFilter || !chrome.showSearch;

/** True when the URL carries no filter of ours — i.e. a bare /plans. */
export const isDefaultFilter = (state: PlansFilterState): boolean =>
  writePlansFilter(new URLSearchParams(), state).toString() === "";
